import React, { useState, useEffect } from 'react';
import { useUsers } from '../../context/UsersContext';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';
import api from '../../utils/api';

export default function ContractManagement() {
  const { currentUser } = useUsers();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.config.getContractTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('Failed to load contract templates:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.textMid }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ 
        ...S.card,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 16 
      }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 4 }}>Contract Templates</h2>
          <p style={{ fontSize: 12, color: C.textMid, margin: 0 }}>
            Manage contract templates that can be assigned to clients during onboarding
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 18px',
            background: C.accent,
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon name="add-circle-outline" style={{ fontSize: 16 }} />
          New Template
        </button>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {templates.map(template => (
          <div key={template.id} style={{
            ...S.card,
            padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <Icon name="document-text" style={{ fontSize: 24, color: C.blue }} />
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, marginBottom: 2 }}>
                      {template.name}
                    </h3>
                    <div style={{ fontSize: 12, color: C.textMid }}>{template.shortName}</div>
                  </div>
                </div>
                <p style={{ fontSize: 13, color: C.textMid, margin: '0 0 12px 36px' }}>
                  {template.description}
                </p>
                <div style={{ display: 'flex', gap: 16, marginLeft: 36, fontSize: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {template.isRequired ? (
                      <>
                        <Icon name="alert-circle" style={{ fontSize: 14, color: C.red }} />
                        <span style={{ color: C.red, fontWeight: 600 }}>Required</span>
                      </>
                    ) : (
                      <>
                        <Icon name="information-circle-outline" style={{ fontSize: 14, color: C.textMid }} />
                        <span style={{ color: C.textMid }}>Optional</span>
                      </>
                    )}
                  </div>
                  {template.requiresCountersignature && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.textMid }}>
                      <Icon name="people-outline" style={{ fontSize: 14 }} />
                      <span>Requires Countersignature</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.textMid }}>
                    <Icon name="layers-outline" style={{ fontSize: 14 }} />
                    <span>Order: {template.sortOrder}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setEditingTemplate(template)}
                  style={{
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Icon name="create-outline" style={{ fontSize: 14 }} />
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          background: C.bg, 
          borderRadius: 12,
          border: `1px solid ${C.border}` 
        }}>
          <Icon name="document-outline" style={{ fontSize: 48, color: C.textLight }} />
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: '16px 0 8px', color: C.text }}>
            No Contract Templates
          </h3>
          <p style={{ fontSize: 13, color: C.textMid, marginBottom: 20 }}>
            Create your first contract template to get started
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 18px',
              background: C.accent,
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="add-circle-outline" style={{ fontSize: 16 }} />
            Create Template
          </button>
        </div>
      )}

      {(showCreateModal || editingTemplate) && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          onSave={() => {
            loadTemplates();
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateModal({ template, onClose, onSave }) {
  const [formData, setFormData] = useState(template || {
    name: '',
    shortName: '',
    description: '',
    content: '',
    isRequired: true,
    requiresCountersignature: false,
    sortOrder: 1,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (template) {
        // Update existing template
        await api.config.updateContractTemplate(template.id, formData);
      } else {
        // Create new template
        await api.config.createContractTemplate(formData);
      }
      
      onSave();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        maxWidth: 800,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: 32,
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>
          {template ? 'Edit Template' : 'New Contract Template'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label style={S.label}>Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={S.input}
                required
                placeholder="e.g., Master Service Agreement"
              />
            </div>

            <div>
              <label style={S.label}>Short Name *</label>
              <input
                type="text"
                value={formData.shortName}
                onChange={(e) => setFormData({ ...formData, shortName: e.target.value })}
                style={S.input}
                required
                placeholder="e.g., MSA"
              />
            </div>

            <div>
              <label style={S.label}>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{ ...S.input, minHeight: 60, fontFamily: 'inherit' }}
                required
                placeholder="Brief description of this contract"
              />
            </div>

            <div>
              <label style={S.label}>Document Content (Markdown) *</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                style={{ ...S.input, minHeight: 200, fontFamily: 'monospace', fontSize: 12 }}
                required
                placeholder="# Contract Title&#10;&#10;Contract content goes here..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={S.label}>Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
                  style={S.input}
                  min="1"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isRequired}
                  onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                />
                <span style={{ fontSize: 13 }}>Required for all clients</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.requiresCountersignature}
                  onChange={(e) => setFormData({ ...formData, requiresCountersignature: e.target.checked })}
                />
                <span style={{ fontSize: 13 }}>Requires countersignature</span>
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: C.text,
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 700,
                color: 'white',
                background: saving ? C.textLight : C.accent,
                border: 'none',
                borderRadius: 8,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
